import { DuplicateIcon } from "@heroicons/react/outline";
import {
  useState,
  useCallback,
  useRef,
  useEffect,
  CSSProperties,
  ReactNode,
} from "react";

const styles: { [key: string]: CSSProperties } = {
  copyButton: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",

    padding: 0,
    margin: 0,
    border: "none",
    outline: "none",
    borderRadius: 4,
    background: "transparent",
    boxShadow: "none",
    cursor: "pointer",
    transition: "color 0.15s ease",
  },
  icon: {
    width: 15,
    display: "block",
    verticalAlign: "middle",
    pointerEvents: "none",
  },

  copiedPopup: {
    position: "absolute",
    left: "50%",
    transform: "translateY(90%)",

    background: "#333",
    color: "white",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 12,
    whiteSpace: "nowrap",

    pointerEvents: "none",
    zIndex: 15,
  },
};

interface CopyTextButtonProps {
  copyText: string;
  label?: string;
  resetDelay?: number;
  className?: string;
  iconOverride?: ReactNode;
}

const CopyTextButton = ({
  copyText,
  label = "Copy to clipboard",
  resetDelay = 1500,
  iconOverride,
}: CopyTextButtonProps) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(copyText);
      } else {
        // Fallback for older browsers / non-secure contexts
        const textarea = document.createElement("textarea");
        textarea.value = copyText;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  }, [copyText]);

  useEffect(() => {
    if (copied) {
      timeoutRef.current = setTimeout(() => setCopied(false), resetDelay);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [copied, resetDelay]);

  return (
    <button
      type="button"
      onClick={handleCopy}
      style={styles.copyButton}
      aria-label={copied ? "Copied!" : label}
      title={label}
    >
      {iconOverride ?? <DuplicateIcon height={"15px"} width={"15px"} />}
      {copied && <span style={styles.copiedPopup}>Copied!</span>}
    </button>
  );
};

export default CopyTextButton;
